import {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';

import FeatureGates, {
  CustomAttributes,
  FeatureGateEnvironment,
  PerimeterType,
  Identifiers,
} from '@atlaskit/feature-gate-js-client';
import { setBooleanFeatureFlagResolver } from '@atlaskit/platform-feature-flags';
import { uncurlyUuid } from '@atlassian/bitkit-analytics';
import { FeatureGatesInitialization } from '@atlassian/feature-gates-react';

import { CurrentDataContext } from 'src/app/current-data';
import { AppDataContext } from 'src/app/data';
import { StatsigKeys } from 'src/config/feature-flag-keys';
import { BbEnv, getBbEnv } from 'src/utils/bb-env';

export type StatsigGateContextType = {
  featureGates: typeof FeatureGates;
};

export const StatsigGateContext = createContext<StatsigGateContextType>({
  featureGates: FeatureGates,
});

export const statsigEnvMap: { [bbEnv in BbEnv]: FeatureGateEnvironment } = {
  [BbEnv.Development]: FeatureGateEnvironment.Development,
  [BbEnv.Test]: FeatureGateEnvironment.Production, // Test is prod-like for application changes
  [BbEnv.Staging]: FeatureGateEnvironment.Staging,
  [BbEnv.Integration]: FeatureGateEnvironment.Staging,
  [BbEnv.Production]: FeatureGateEnvironment.Production,
};

export const statsigOptions = (function () {
  const env = statsigEnvMap[getBbEnv() || BbEnv.Development];
  const apiKey = StatsigKeys[env];

  return {
    apiKey,
    environment: env,
    targetApp: 'bitbucket-cloud_web',
    perimeter: PerimeterType.COMMERCIAL,
  };
})();

type FeatureIdsAndAttributes = {
  identifiers: Identifiers;
  customAttributes: CustomAttributes;
};

export const useFeatureIdsAndAttributes = (
  forceUpdate: boolean = false
): FeatureIdsAndAttributes => {
  const appData = useContext(AppDataContext);
  const currentData = useContext(CurrentDataContext);

  const customAttributes = useMemo(() => {
    return {
      atlassian_staff: appData.user?.isAtlassian ?? false,
      bitbucket_team: appData.user?.isBitbucket ?? false,
    };
  }, [appData.user?.isAtlassian, appData.user?.isBitbucket]);
  const identifiers = useMemo(() => {
    return {
      atlassianAccountId: appData.user?.aaid ?? undefined,
      bitbucketRepositoryId: currentData.repository?.uuid
        ? uncurlyUuid(currentData.repository.uuid)
        : undefined,
      bitbucketWorkspaceId: currentData.workspace?.uuid
        ? uncurlyUuid(currentData.workspace.uuid)
        : undefined,
    };
  }, [
    appData.user?.aaid,
    currentData.repository?.uuid,
    currentData.workspace?.uuid,
  ]);
  const identifiersRef = useRef<Identifiers>(identifiers);

  useEffect(() => {
    // If we haven't been initialized yet we can exit early
    if (!FeatureGates.initializeCompleted()) {
      return;
    }

    // If identifiers haven't changed, don't do anything. There is a
    // FeatureGates.isCurrentUser method, but we can't use it. We have to pass
    // only the customAttributes we know about, but there might be additional
    // custom attributes from the initialization request that we dont know
    // about. So we check against the ref.
    if (identifiersRef.current === identifiers) {
      return;
    }

    // We have new identifiers or custom attributes, so we need to update the
    // user. This will make a new request to Statsig, any feature updates will
    // be handled by the FeatureGates subscriptions.
    FeatureGates.updateUser(statsigOptions, identifiers, customAttributes)
      .then(() => {
        identifiersRef.current = identifiers;
      })
      .catch(() => {
        // This is not expected to fail but could due to a network error. We
        // expect this to get called often enough that we can just wait for the
        // next attempt.
      });
  }, [identifiers, customAttributes]);

  const staticIdentifiers = useMemo(() => {
    // Always return the initial identifiers to avoid re-rendering
    return identifiers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const featureIdentifiers = !forceUpdate ? staticIdentifiers : identifiers;

  return {
    identifiers: featureIdentifiers,
    customAttributes,
  };
};

export const StatsigFeatureProvider = ({
  children,
}: React.PropsWithChildren) => {
  // This feature gate wrapper blocks rendering while waiting on requests to
  // Statsig. Every time we switch routes where the identifiers change, we make
  // a new request and must re-render all children. Because we place this
  // component at the top of the tree, it causes the navigation component to
  // re-render, which breaks Rovo state. What we want to do is block rendering
  // on the first request, then rely on FeatureGate subscriptions to handle any
  // changes. To do this, we need to pass the exact same `identifiers` and
  // `customAttributes` (shallow equality) and manually call the
  // `FeatureGates.updateUser` method when they change.
  const { identifiers, customAttributes } = useFeatureIdsAndAttributes();
  const appData = useContext(AppDataContext);
  const [version, setVersion] = useState(0);

  const flushFeatureOverrides = useCallback(() => {
    FeatureGates.clearAllOverrides();
    setVersion(prev => prev + 1);
  }, []);

  const toggleFeatureOverride = useCallback(
    (featureId: string, overrideValue?: boolean) => {
      FeatureGates.overrideGate(
        featureId,
        typeof overrideValue !== 'undefined'
          ? overrideValue
          : !FeatureGates.checkGate(featureId)
      );
      setVersion(prev => prev + 1);
    },
    []
  );

  useEffect(() => {
    if (customAttributes?.atlassian_staff) {
      // Initialize window.bb if it doesn't exist
      window.bb = window.bb || {};

      // Add the feature gates methods to the window.bb object
      window.bb.flushFeatureOverrides = flushFeatureOverrides;
      window.bb.toggleFeatureOverride = toggleFeatureOverride;
    }

    // Clean up on unmount
    return () => {
      delete window.bb?.flushFeatureOverrides;
      delete window.bb?.toggleFeatureOverride;
    };
  }, [
    customAttributes?.atlassian_staff,
    flushFeatureOverrides,
    toggleFeatureOverride,
  ]);

  useEffect(() => {
    setBooleanFeatureFlagResolver((flagKey: string) => {
      return FeatureGates.checkGate(flagKey);
    });
  }, [appData.features]);

  return (
    <FeatureGatesInitialization
      key={version}
      options={statsigOptions}
      customAttributes={customAttributes}
      identifiers={identifiers}
    >
      {children}
    </FeatureGatesInitialization>
  );
};
